import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/page";
import { DocumentParents } from "@/components/documents/DocumentParents";
import { useDisableTeam } from "@/components/page/disableTeam";
import { useUserStore } from "@/stores/userStore";
import { useState } from "react";
import { RoleProtected } from "@/components/common/RoleProtected";
import { Button } from "@repo/ui/components/Base/Button";
import { DocumentForm } from "@/components/documents/DocumentForm";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { useGetDocument } from "@/hooks/queryHooks/documentHooks";
import { useGetTenantByFriendly } from "@/hooks/queryHooks/tenantHooks";
import { documentRepo } from "@/repositories/documentRepo";

export const DocumentPage = () => {
  const { tenantId, documentId } = useParams();
  const navigate = useNavigate();
  const activeTeam = useUserStore((s) => s.activeTeam);
  const activeTeamId =
    activeTeam && activeTeam !== "org" ? activeTeam.id : "org";

  const [formKey, setFormKey] = useState(0);
  const resetForm = () => setFormKey((key) => key + 1);

  useDisableTeam();

  const { data: tenantData } = useGetTenantByFriendly(tenantId ?? "");
  const { data: documentData } = useGetDocument(
    tenantData?.id ?? "",
    documentId ?? ""
  );

  const teamMismatch = documentData?.teamId != activeTeamId;

  const [documentViewOnly, setDocumentViewOnly] = useState(true);

  if (!documentData || !tenantData) return null;

  const handleEditDocument = () => {
    setDocumentViewOnly(false);
  };

  const handleComplete = () => {
    setDocumentViewOnly(true);
  };

  const handleCancel = () => {
    setDocumentViewOnly(true);
    resetForm();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const originalAttachment = documentRepo.getAttachmentByType(
    documentData,
    "original"
  );

  const hasPdfAttachment =
    !originalAttachment?.isUrl &&
    originalAttachment?.attachmentId &&
    originalAttachment.extension?.toLowerCase() === "pdf";

  // If the document was prepped for signing
  const signPrepped = !!documentData.submissionTemplate;
  const canOpenSigningPrep = hasPdfAttachment || signPrepped;

  // If the document was signed
  const signedDocument =
    documentData.submissionTemplate &&
    documentData.submissionTemplate.status !== "draft";

  return (
    <div className="flex flex-col flex-grow">
      <PageHeader title={`${tenantData.name}: ${documentData.name}`}>
        {" "}
      </PageHeader>
      <RoleProtected minRole="edit">
        <div className="flex flex-row justify-end pb-2 gap-2">
          <Button
            tooltip={
              teamMismatch
                ? "You are in the wrong team to be editing this document"
                : "Edit Document"
            }
            onClick={handleEditDocument}
            disabled={teamMismatch || !documentViewOnly}
          >
            Edit
          </Button>
          {tenantData.featureFlags?.signatures && (
            <Button
              disabled={!canOpenSigningPrep}
              tooltip={
                !canOpenSigningPrep
                  ? "PDF Must Be Attached"
                  : signPrepped
                    ? "Edit Signing Prep"
                    : "Send Sign Requests"
              }
              onClick={async () => {
                navigate(
                  new AppUrlPath(tenantData.id).getDocumentSignPath(
                    `${documentData.id}`
                  )
                );
              }}
            >
              {!signPrepped
                ? "Begin Signing Prep"
                : !signedDocument
                  ? "Edit Signing Prep"
                  : "View Signatures"}
            </Button>
          )}
        </div>
      </RoleProtected>
      {tenantData?.id && (
        <div className="flex flex-row gap-4 justify-center">
          <div className="w-0 flex-grow-[2]">
            <DocumentForm
              key={formKey}
              viewOnly={documentViewOnly}
              tenantId={tenantData.id}
              documentFormData={documentData}
              handleCancel={handleCancel}
              handleComplete={handleComplete}
              handleGoBack={handleGoBack}
            />
          </div>
          <div className="w-0 flex-grow">
            <DocumentParents document={documentData} tenantId={tenantData.id} />
          </div>
        </div>
      )}
    </div>
  );
};
